import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import ProgressWizard from 'components/field/ProgressWizard';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Card from 'components/global/Card';
import Image from 'components/global/Image';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import DynamicList from 'components/list/DynamicList';
import { FilterType } from 'components/list/DynamicListFilterUtils';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { ADVANCE_WMS_MODULE, AUTOCOMPLETE, ENUMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';

const SelectOrder = ({ selectedRowOrders, setSelectedRowOrders }) => {
	const [numOfSku, setNumOfSku] = useState(0);
	const [numOfOrders, setNumOfOrders] = useState(0);
	const [totalQty, setTotalQty] = useState(0);
	const formMethods = useFormContext();

	const { watch, setValue, getValues } = formMethods;

	const columns = useMemo(
		() => [
			{
				Header: 'id',
				accessor: 'id',
				show: false,
				filterType: FilterType.string
			},
			{
				Header: 'orderID',
				accessor: 'orderID',
				filterType: FilterType.string
			},
			{
				Header: 'skus',
				accessor: 'skuList',
				filterType: FilterType.string
			},
			{
				Header: 'quantities',
				accessor: 'qtyList',
				filterType: FilterType.number
			},
			{
				Header: 'totalQuantity',
				accessor: 'totalQty',
				filterType: FilterType.float
			}
		],
		[watch('warehouseId'), watch('typeOfOrders')]
	);

	useEffect(() => {
		setValue('orderIds', []);
		setValue('ids', []);
		setNumOfSku(0);
		setNumOfOrders(0);
		setTotalQty(0);
		setSelectedRowOrders([]);
	}, [watch('warehouseId')]);

	const table = useMemo(
		() => (
			<DynamicList
				id={DYNAMICLISTS.WAVE_PILOT_LIST}
				key={watch('warehouseId')}
				showColumnCalculation={false}
				columns={columns}
				fetchUrl={ADVANCE_WMS_MODULE.GET_ORDERS_BY_WAREHOUSE_AND_ORDER_TYPES}
				selectedRows={watch('warehouseId') !== selectedRowOrders[0]?.warehouseID ? [] : selectedRowOrders}
				onSelectionChange={(selectedRows) => {
					const listOfSkus = selectedRows.flatMap((order) =>
						order.skuList ? order.skuList.split(', ') : []
					);
					const uniqueSkus = [...new Set(listOfSkus)];
					setNumOfSku(uniqueSkus.length);
					setNumOfOrders(selectedRows.map((order) => order.orderID).length);
					setTotalQty(selectedRows.map((order) => order.totalQty).reduce((acc, curr) => acc + curr, 0));
					setValue(
						'orderIds',
						selectedRows.map((order) => order.orderID)
					);
					setValue(
						'ids',
						selectedRows.map((order) => order.id)
					);
					setSelectedRowOrders(selectedRows);
				}}
				showCheckbox={true}
				sendRequestWithColumns={false}
				requestParameters={{
					orderType: [getValues('typeOfOrders')],
					warehouseID: [getValues('warehouseId')]
				}}
			/>
		),
		[watch('warehouseId'), watch('typeOfOrders')]
	);

	return (
		<>
			<div style={{ padding: '1rem', width: '100%' }}>
				<Typography variant="p2" fontWeight="500">
					{t('waveCreationPilot.filterOptions')}
				</Typography>
				<div
					style={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						margin: '2rem 0 1rem',
						gap: '1rem'
					}}>
					<FormItem label="warehouse" required style={{ width: 'calc(50% - 1rem)', padding: '0' }}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							name="warehouseId"
							value={watch('warehouseId')}
							setValue={(value) => {
								setValue('warehouseId', value?.ID ?? null);
							}}
							columns={[{ accessor: 'Name', width: 200 }]}
							selectionLabelAccessor={['Name']}
						/>
					</FormItem>
					<FormItem
						label={t('waveCreationPilot.fields.typeOfOrders')}
						style={{ width: 'calc(50% - 1rem)', padding: '0' }}>
						<EnumSelect
							disabled={!getValues('warehouseId')}
							name="typeOfOrders"
							value={watch('typeOfOrders')}
							valueRemovable={true}
							searchable={false}
							enumName={ENUMS.WAVE_CREATION_TYPE_OF_ORDERS}
							setValue={(value) => {
								setValue('typeOfOrders', value ?? '');
							}}
						/>
					</FormItem>
				</div>
			</div>
			<div
				style={{
					display: 'flex',
					flexDirection: 'row',
					width: '100%',
					justifyContent: 'space-between',
					gap: '1rem',
					margin: '1rem 0',
					padding: '0 1rem'
				}}>
				<Card style={{ width: '33%', display: 'flex', direction: 'row', gap: '2rem' }}>
					<Image url="https://storage.googleapis.com/tryoto-public/public/assets/images/numberOfSKU.svg" />
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<div>
							<Typography variant="p2">{t('wcp.previewCards.numOfSku')}</Typography>
							<Typography variant="h2">{numOfSku}</Typography>
						</div>
					</div>
				</Card>
				<Card style={{ width: '33%', display: 'flex', direction: 'row', gap: '2rem' }}>
					<Image url="https://storage.googleapis.com/tryoto-public/public/assets/images/numebrOfOrders.svg" />
					<div>
						<Typography variant="p2">{t('wcp.previewCards.numOfOrders')}</Typography>
						<Typography variant="h2">{numOfOrders}</Typography>
					</div>
				</Card>
				<Card style={{ width: '33%', display: 'flex', direction: 'row', gap: '2rem' }}>
					<Image url="https://storage.googleapis.com/tryoto-public/public/assets/images/totalQTY.svg" />
					<div>
						<Typography variant="p2">{t('wcp.previewCards.totalQty')}</Typography>
						<Typography variant="h2">{totalQty}</Typography>
					</div>
				</Card>
			</div>
			<FormItem>{table}</FormItem>
		</>
	);
};

const CreateWave = () => {
	const WaveTypes = {
		wave: 'Wave',
		zoneWave: 'Zone Wave'
	};

	const [isLoading] = useState(false);

	const formMethods = useFormContext();

	const { watch, setValue, getValues } = formMethods;

	return (
		<>
			<FormItem label={'orderIds'} isLoading={isLoading} required style={{ width: '50%' }}>
				<StringInput disabled value={watch('orderIds').join(', ')} />
			</FormItem>
			<FormItem label={t('waveCreationPilot.fields.waveType')} style={{ width: '50%' }} required>
				<EnumSelect
					name="waveType"
					value={watch('waveType')}
					valueRemovable={false}
					searchable={false}
					data={Object.values(WaveTypes)}
					setValue={(value) => {
						setValue('waveType', value);
					}}
				/>
			</FormItem>
			<FormItem
				label={'assignedPicker'}
				isLoading={isLoading}
				style={{ width: '50%' }}
				required={getValues('waveType') === WaveTypes.wave}>
				<AutoComplete
					autoCompleteName={AUTOCOMPLETE.PICKER_USERS_BY_WAREHOUSE}
					name="assignedPickerID"
					value={watch('assignedPickerID')}
					setValue={(value) => {
						setValue('assignedPickerID', value?.ID ?? null);
					}}
					columns={[{ accessor: 'Name', width: 200 }]}
					selectionLabelAccessor={['Name']}
					filterObject={{
						warehouseID: getValues('warehouseId')
					}}
					disabled={getValues('waveType') === WaveTypes.zoneWave}
				/>
			</FormItem>
			<FormItem label={t('waveCreationPilot.fields.isSortingNeeded')} style={{ width: '50%' }}>
				<SwitchInput name="sortingRequired" />
			</FormItem>
		</>
	);
};

const WaveCreationPilotModal = NiceModal.create(() => {
	const { t } = useTranslation();

	const modal = useModal();

	const [tab, setTab] = useState(0);
	const [isMinimizing, setIsMinimizing] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const { showError, showSuccess } = useAlert();
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();

	const schema = useMemo(
		() => [
			yup.object().shape({
				warehouseId: yup.string().nullable().required(),
				typeOfOrders: yup.string(),
				orderIds: yup.array().of(yup.string().nullable()).min(1),
				ids: yup.array().of(yup.string().nullable()).min(1)
			}),
			yup.object().shape({
				waveType: yup.string().nullable().required(),
				assignedPickerID: yup
					.string()
					.nullable()
					.when(['waveType'], {
						is: (waveType) => waveType === 'Wave',
						then: (schema) => schema.required()
					}),
				sortingRequired: yup.boolean().required()
			})
		],
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema[tab]),
		defaultValues: {
			warehouseId: null,
			typeOfOrders: '',
			orderIds: [],
			assignedPickerID: null,
			isSortingRequired: false,
			waveType: null,
			ids: []
		}
	});

	const {
		handleSubmit,
		formState: { isDirty, isSubmitting, isSubmitSuccessful },
		reset,
		getValues
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful && !isMinimizing);

	const [selectedRowOrders, setSelectedRowOrders] = useState([]);

	useEffect(() => {
		setSelectedRowOrders([]);
	}, [getValues('warehouseId')]);

	const progressWizardTabs = () => {
		switch (tab) {
			case 0:
				return (
					<Form formMethods={formMethods}>
						<SelectOrder
							selectedRowOrders={selectedRowOrders}
							setSelectedRowOrders={setSelectedRowOrders}
						/>
					</Form>
				);
			case 1:
				return (
					<Form borderless formMethods={formMethods}>
						<CreateWave />
					</Form>
				);
			default:
				return null;
		}
	};

	const handleCloseModal = () => modal.remove();

	const startFlowFromTheBeginning = () => {
		reset();
		setTab(0);
	};

	const onSubmit = async (data) => {
		setIsLoading(true);
		try {
			const resp = await axiosPrivate.post(ADVANCE_WMS_MODULE.CREATE_WAVE_FROM_WAVE_CREATION_PILOT, {
				ids: data.ids,
				assignedPickerID: data.assignedPickerID,
				whId: data.warehouseId.toString(),
				waveType: data.waveType.toString(),
				sortingRequirement: data.sortingRequired
			});
			if (data.waveType === 'Wave') {
				dispatch(updateDynamicListsInPage());
			}
			startFlowFromTheBeginning();
			if (
				resp.data.success &&
				(!resp.data.failedIds ||
					(resp.data.failedIds && Array.isArray(resp.data.failedIds) && resp.data.failedIds.length === 0))
			) {
				showSuccess();
				if (resp.data.waves && Array.isArray(resp.data.waves)) {
					let result = '';
					resp.data.waves.forEach((wave) => (result += `${wave.waveNumber},`));
					if (result.length > 0) result = result.slice(0, -1);
				}
			} else {
				showError({ message: 'errorMsg.cannotCreateWaveForSomeItems' });
			}
		} catch (error) {
			AppLogger.error(error);
			showError({
				header: 'error',
				message: error.response.data.code
			});
		} finally {
			setIsLoading(false);
		}
	};

	const prevStep = () => {
		if (tab > 0) {
			setTab(tab - 1);
		}
	};

	const nextStep = () => {
		setTab(tab + 1);
	};

	const warehouseId = formMethods.watch('warehouseId');

	useEffect(() => {
		if (warehouseId === null) {
			reset();
		}
	}, [warehouseId]);

	return (
		<Modal
			fullwidth
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={t('waveCreationPilot.minimizedLabel')}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}
			name={'wave-pilot-form'}>
			<ModalTitle>
				<Typography variant="p1">{t('waveCreationPilot.modalTitle')}</Typography>
				<ProgressWizard
					clickable={false}
					stages={[
						{ name: t('waveCreationPilot.selectOrderTabName') },
						{ name: t('waveCreationPilot.createWaveTabName') }
					]}
					setIndex={setTab}
					index={tab}
					gutterBottom
				/>
			</ModalTitle>
			<ModalContent>{progressWizardTabs()}</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					variant="blackOutline"
					onClick={handleCloseModal}
					css={css`
						text-decoration: underline;
						border: none !important;
					`}
				/>
				{tab !== 0 && (
					<Button label="previous" variant="outline" icon={faAngleLeft} onClick={() => prevStep()} />
				)}
				{tab === 0 && (
					<Button label={'continue'} icon={faAngleRight} iconPos={'right'} onClick={handleSubmit(nextStep)} />
				)}
				{tab === 1 && (
					<Button
						loading={isLoading}
						label={t('create')}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default WaveCreationPilotModal;
